import React from 'react';

/**
 * Small dependency-free SVG charts for the Five-Year Forecast dashboard.
 * Everything scales to its container width (viewBox), styled like the
 * click-through: navy / gold palette, light grid, compact $ axis labels.
 */

export const CHART_COLORS = ['#00205c', '#b68400', '#4f6d9a', '#c9a94d', '#8a94a6', '#2e7d5b'];
const NEG = '#b42318';
const GRID = '#e5e9ef';
const TEXT = '#667085';

export interface ChartSeries { name: string; values: number[]; color?: string }

/** $1.2M / $340K / ($45K) */
export const compactMoney = (v: number): string => {
    const a = Math.abs(v);
    let s: string;
    if (a >= 1e9) s = `$${(a / 1e9).toFixed(a >= 1e10 ? 0 : 1)}B`;
    else if (a >= 1e6) s = `$${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M`;
    else if (a >= 1e3) s = `$${(a / 1e3).toFixed(a >= 1e4 ? 0 : 1)}K`;
    else s = `$${Math.round(a)}`;
    return v < 0 ? `(${s})` : s;
};

/** Rounded axis bounds and ~4 ticks that always include 0. */
export const niceScale = (values: number[]) => {
    let min = Math.min(0, ...values);
    let max = Math.max(0, ...values);
    if (min === max) max = min + 1;
    const raw = (max - min) / 4;
    const mag = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || raw;
    min = Math.floor(min / step) * step;
    max = Math.ceil(max / step) * step;
    const ticks: number[] = [];
    for (let v = min; v <= max + step / 2; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
    return { min, max, ticks };
};

const W = 640;
const H = 260;
const PAD = { top: 12, right: 12, bottom: 30, left: 62 };

const Legend: React.FC<{ series: ChartSeries[] }> = ({ series }) => (
    <div className="fyf-legend">
        {series.map((s, i) => (
            <span key={s.name}><i style={{ background: s.color || CHART_COLORS[i % CHART_COLORS.length] }} />{s.name}</span>
        ))}
    </div>
);

const Axes: React.FC<{ ticks: number[]; y: (v: number) => number }> = ({ ticks, y }) => (
    <g>
        {ticks.map((t) => (
            <g key={t}>
                <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} stroke={t === 0 ? '#c3cad5' : GRID} />
                <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill={TEXT}>{compactMoney(t)}</text>
            </g>
        ))}
    </g>
);

/** Grouped vertical bars: one group per category (e.g. Year 1-5), one bar per series. */
export const GroupedBarChart: React.FC<{ categories: string[]; series: ChartSeries[]; title?: string }> = ({ categories, series, title }) => {
    const all = series.flatMap((s) => s.values);
    const { min, max, ticks } = niceScale(all);
    const plotH = H - PAD.top - PAD.bottom;
    const y = (v: number) => PAD.top + ((max - v) / (max - min)) * plotH;
    const groupW = (W - PAD.left - PAD.right) / Math.max(categories.length, 1);
    const barW = Math.max(4, (groupW * 0.75) / Math.max(series.length, 1));
    return (
        <div>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={title}>
                <Axes ticks={ticks} y={y} />
                {categories.map((c, ci) => {
                    const x0 = PAD.left + ci * groupW + (groupW - barW * series.length) / 2;
                    return (
                        <g key={`${c}-${ci}`}>
                            {series.map((s, si) => {
                                const v = s.values[ci] || 0;
                                const top = y(Math.max(v, 0));
                                const h = Math.abs(y(v) - y(0));
                                return (
                                    <rect key={s.name} x={x0 + si * barW} y={top} width={barW - 2} height={Math.max(h, 0.5)}
                                        fill={v < 0 ? NEG : (s.color || CHART_COLORS[si % CHART_COLORS.length])} rx="2">
                                        <title>{`${c} · ${s.name}: ${compactMoney(v)}`}</title>
                                    </rect>
                                );
                            })}
                            <text x={PAD.left + ci * groupW + groupW / 2} y={H - 10} textAnchor="middle" fontSize="11" fill={TEXT}>{c}</text>
                        </g>
                    );
                })}
            </svg>
            <Legend series={series} />
        </div>
    );
};

/** Lines over a label axis (e.g. Year 1-5 or 12 months), with point markers. */
export const LineChart: React.FC<{ labels: string[]; series: ChartSeries[]; title?: string }> = ({ labels, series, title }) => {
    const { min, max, ticks } = niceScale(series.flatMap((s) => s.values));
    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;
    const x = (i: number) => PAD.left + (labels.length <= 1 ? plotW / 2 : (i / (labels.length - 1)) * plotW);
    const y = (v: number) => PAD.top + ((max - v) / (max - min)) * plotH;
    return (
        <div>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={title}>
                <Axes ticks={ticks} y={y} />
                {labels.map((l, i) => (
                    <text key={`${l}-${i}`} x={x(i)} y={H - 10} textAnchor="middle" fontSize="11" fill={TEXT}>{l}</text>
                ))}
                {series.map((s, si) => {
                    const color = s.color || CHART_COLORS[si % CHART_COLORS.length];
                    const pts = s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ');
                    return (
                        <g key={s.name}>
                            <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" />
                            {s.values.map((v, i) => (
                                <circle key={i} cx={x(i)} cy={y(v)} r="4" fill={v < 0 ? NEG : '#fff'} stroke={color} strokeWidth="2">
                                    <title>{`${labels[i]} · ${s.name}: ${compactMoney(v)}`}</title>
                                </circle>
                            ))}
                        </g>
                    );
                })}
            </svg>
            {series.length > 1 && <Legend series={series} />}
        </div>
    );
};

/** Horizontal bars, largest first (e.g. top expense lines). */
export const HorizontalBars: React.FC<{ items: { label: string; value: number }[]; emptyText?: string }> = ({ items, emptyText }) => {
    if (!items.length) return <div className="fyf-meta p-3">{emptyText}</div>;
    const max = Math.max(...items.map((i) => Math.abs(i.value)), 1);
    return (
        <div className="fyf-hbars">
            {items.map((it, i) => (
                <div className="fyf-hbar" key={`${it.label}-${i}`}>
                    <span className="lbl" title={it.label}>{it.label}</span>
                    <span className="track">
                        <span className="fill" style={{ width: `${(Math.abs(it.value) / max) * 100}%`, background: i === 0 ? CHART_COLORS[1] : CHART_COLORS[0] }} />
                    </span>
                    <span className="val">{compactMoney(it.value)}</span>
                </div>
            ))}
        </div>
    );
};
